import { useEffect } from 'react';
import type { ComponentType, PropsWithChildren } from 'react';
import type React from 'react';

import { useI18n } from '@change-corgi/core-react/i18n';
import { TrackingContext } from '@change-corgi/core-react/tracking';

import { fromTemplate } from '#src/shared/pageHead';

import { getPetitionGuidesCategoryPageTitle } from '#src/app/pages/petitionGuides/category/details/shared/pageTitle';
import type { ErrorStateDetails } from '#src/app/shared/utils/async';
import { isError, isLoaded } from '#src/app/shared/utils/async';

import type { PetitionGuidesCategoryPageCombinedState, PetitionGuidesCategoryPagePrefetchedContext } from './hook';
import { usePageContext } from './hook';
import { PetitionGuidesCategoryPageDataContextProvider } from './pageData';

export type ErrorHandlerProps = { context: ErrorStateDetails<PetitionGuidesCategoryPageCombinedState> };

type Props = {
	slug: string;
	prefetchedData: PetitionGuidesCategoryPagePrefetchedContext | undefined;
	errorHandler: ComponentType<ErrorHandlerProps>;
	loadingFallback: JSX.Element;
};

export const PetitionGuidesCategoryPageContextProvider: React.FC<PropsWithChildren<Props>> = ({
	children,
	slug,
	prefetchedData,
	errorHandler: ErrorHandler,
	loadingFallback,
}) => {
	const pageContextState = usePageContext(slug, prefetchedData);
	const i18n = useI18n();

	const currentPageData = isLoaded(pageContextState) ? pageContextState.pageData.data : undefined;

	// react-router does not revalidate its loader for a query-string-only navigation (see
	// `shouldRevalidate` in createRoutes.tsx), so the router-level title effect never reruns when
	// `?page=N` changes client-side — it keeps setting the title from the page's first-load data.
	// Keep the tab title in sync with the live, client-refetched page data ourselves.
	useEffect(() => {
		if (!currentPageData) return;
		document.title = fromTemplate(getPetitionGuidesCategoryPageTitle(currentPageData, i18n)).title;
	}, [currentPageData, i18n]);

	if (isError(pageContextState)) {
		const { status, ...rest } = pageContextState;
		return <ErrorHandler context={rest} />;
	}
	if (!isLoaded(pageContextState)) return loadingFallback;

	const { pageData } = pageContextState;

	return (
		<PetitionGuidesCategoryPageDataContextProvider pageData={pageData}>
			<TrackingContext properties={{ category_slug: pageData.data.slug, current_page: pageData.data.page }}>
				{children}
			</TrackingContext>
		</PetitionGuidesCategoryPageDataContextProvider>
	);
};
